import React, { useState } from "react";
import { Share2, Copy, Check, X, Loader2, Link as LinkIcon, Trash2, Clock, AlertTriangle, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Admin button placed next to "Preview video" — generates / shows the public
 * share link (/preview/{token}) so the makelaar can view the preview & comment
 * without logging in. Supports configurable expiry (7/14/30/90 days or never).
 */
const TOKEN_FIELD_BY_ASSET = {
    preview: "preview_share_token",
    reel_preview: "reel_preview_share_token",
    final_video: "final_video_share_token",
    reel: "reel_share_token",
    photos: "ai_photos_share_token",
};

const EXPIRES_FIELD_BY_ASSET = {
    preview: "preview_share_expires_at",
    reel_preview: "reel_preview_share_expires_at",
    final_video: "final_video_share_expires_at",
    reel: "reel_share_expires_at",
    photos: "ai_photos_share_expires_at",
};

const VIEW_COUNT_FIELD_BY_ASSET = {
    preview: "preview_share_view_count",
    reel_preview: "reel_preview_share_view_count",
    final_video: "final_video_share_view_count",
    reel: "reel_share_view_count",
    photos: "ai_photos_share_view_count",
};

const LAST_VIEWED_FIELD_BY_ASSET = {
    preview: "preview_share_last_viewed_at",
    reel_preview: "reel_preview_share_last_viewed_at",
    final_video: "final_video_share_last_viewed_at",
    reel: "reel_share_last_viewed_at",
    photos: "ai_photos_share_last_viewed_at",
};

const LABEL_BY_ASSET = {
    preview: "Preview",
    reel_preview: "Reel preview",
    final_video: "Finale 4K video",
    reel: "Finale reel",
    photos: "Fotoreeks",
};

const EXPIRY_OPTIONS = [
    { value: 7, label: "7 dagen" },
    { value: 14, label: "14 dagen" },
    { value: 30, label: "30 dagen" },
    { value: 90, label: "90 dagen" },
    { value: 365, label: "1 jaar" },
    { value: 0, label: "Nooit" },
];

function fmtExpiry(iso) {
    if (!iso) return null;
    try {
        const dt = new Date(iso);
        return dt.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch { return null; }
}

function fmtRelative(iso) {
    if (!iso) return null;
    const dt = new Date(iso);
    if (isNaN(dt)) return null;
    const diffMs = Date.now() - dt.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "zojuist";
    if (mins < 60) return `${mins} min geleden`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}u geleden`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d geleden`;
    return dt.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function daysLeft(iso) {
    if (!iso) return null;
    const dt = new Date(iso);
    if (isNaN(dt)) return null;
    const diff = Math.ceil((dt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diff;
}

export default function PreviewShareButton({ req, asset = "preview", onUpdated }) {
    const tokenField = TOKEN_FIELD_BY_ASSET[asset] || "preview_share_token";
    const expiresField = EXPIRES_FIELD_BY_ASSET[asset] || "preview_share_expires_at";
    const viewCountField = VIEW_COUNT_FIELD_BY_ASSET[asset] || "preview_share_view_count";
    const lastViewedField = LAST_VIEWED_FIELD_BY_ASSET[asset] || "preview_share_last_viewed_at";
    const [open, setOpen] = useState(false);
    const [token, setToken] = useState(req?.[tokenField] || null);
    const [expiresAt, setExpiresAt] = useState(req?.[expiresField] || null);
    const [viewCount, setViewCount] = useState(req?.[viewCountField] || 0);
    const [lastViewedAt, setLastViewedAt] = useState(req?.[lastViewedField] || null);
    const [selectedDays, setSelectedDays] = useState(30);
    const [generating, setGenerating] = useState(false);
    const [updatingExpiry, setUpdatingExpiry] = useState(false);
    const [revoking, setRevoking] = useState(false);
    const [copied, setCopied] = useState(false);

    // Resync state when the parent request prop changes
    React.useEffect(() => {
        setToken(req?.[tokenField] || null);
        setExpiresAt(req?.[expiresField] || null);
        setViewCount(req?.[viewCountField] || 0);
        setLastViewedAt(req?.[lastViewedField] || null);
    }, [req, tokenField, expiresField, viewCountField, lastViewedField]);

    const publicUrl = token ? `${window.location.origin}/api/preview-meta/${token}` : "";
    const assetLabel = LABEL_BY_ASSET[asset] || "Preview";
    const expiryLabel = fmtExpiry(expiresAt);
    const left = daysLeft(expiresAt);
    const isExpired = left !== null && left < 0;

    const generate = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post(`/requests/${req.id}/preview-share-link`, { expires_in_days: selectedDays }, { params: { asset } });
            setToken(data.token);
            setExpiresAt(data.expires_at || null);
            setViewCount(data.view_count || 0);
            setLastViewedAt(data.last_viewed_at || null);
            onUpdated?.({ ...req, [tokenField]: data.token, [expiresField]: data.expires_at || null, [viewCountField]: data.view_count || 0, [lastViewedField]: data.last_viewed_at || null });
            toast.success("Deellink aangemaakt");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aanmaken mislukt");
        }
        setGenerating(false);
    };

    const updateExpiry = async (days) => {
        setUpdatingExpiry(true);
        try {
            const { data } = await api.post(`/requests/${req.id}/preview-share-link`, { expires_in_days: days }, { params: { asset } });
            setExpiresAt(data.expires_at || null);
            setViewCount(data.view_count ?? viewCount);
            setLastViewedAt(data.last_viewed_at ?? lastViewedAt);
            onUpdated?.({ ...req, [tokenField]: data.token, [expiresField]: data.expires_at || null, [viewCountField]: data.view_count ?? viewCount, [lastViewedField]: data.last_viewed_at ?? lastViewedAt });
            toast.success(days === 0 ? "Verloopt nooit meer" : `Verloopt over ${days} dagen`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bijwerken mislukt");
        }
        setUpdatingExpiry(false);
    };

    const copy = async () => {
        if (!publicUrl) return;
        let ok = false;
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(publicUrl);
                ok = true;
            }
        } catch { ok = false; }
        if (!ok) {
            try {
                const ta = document.createElement("textarea");
                ta.value = publicUrl;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                ok = document.execCommand("copy");
                document.body.removeChild(ta);
            } catch { ok = false; }
        }
        if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
            toast.success("Link gekopieerd");
        } else {
            toast.error("Kopiëren mislukt — kopieer handmatig");
        }
    };

    const revoke = async () => {
        if (!window.confirm("Weet je zeker dat je deze deellink wil intrekken? De link werkt daarna niet meer.")) return;
        setRevoking(true);
        try {
            await api.delete(`/requests/${req.id}/preview-share-link`, { params: { asset } });
            setToken(null);
            setExpiresAt(null);
            setViewCount(0);
            setLastViewedAt(null);
            onUpdated?.({ ...req, [tokenField]: null, [expiresField]: null, [viewCountField]: 0, [lastViewedField]: null });
            toast.success("Deellink ingetrokken");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Mislukt");
        }
        setRevoking(false);
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs px-3 h-9 border border-[#E5A93D]/40 text-[#E5A93D] hover:bg-[#E5A93D]/10 rounded-sm transition active:scale-95"
                data-testid="preview-share-btn"
            >
                <Share2 className="h-3.5 w-3.5" /> Delen
                {token && viewCount > 0 && (
                    <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-mono px-1.5 h-5 rounded-sm bg-[#E5A93D]/15 border border-[#E5A93D]/30" data-testid="preview-share-view-badge">
                        <Eye className="h-2.5 w-2.5" />{viewCount}
                    </span>
                )}
            </button>

            {open && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
                    <div className="bg-[#FFFFFF] border border-zinc-200 rounded-md max-w-lg w-full p-6 lg:p-8" onClick={(e) => e.stopPropagation()} data-testid="preview-share-modal">
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <div>
                                <h3 className="font-display font-bold text-2xl tracking-tight">{assetLabel} delen</h3>
                                <p className="text-sm text-zinc-500 mt-1">{asset === "photos" ? "Stuur deze link naar de makelaar of klant zodat ze alle foto's kunnen bekijken en downloaden als ZIP. Geen login nodig." : asset === "final_video" || asset === "reel" ? "Stuur deze link naar de makelaar of klant zodat ze de afgewerkte video kunnen bekijken en downloaden. Geen login nodig." : "Stuur deze link naar de makelaar of klant. Geen login nodig — opmerkingen verschijnen automatisch in dit project."}</p>
                            </div>
                            <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-950 p-1" aria-label="Sluiten">
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {!token ? (
                            <div className="py-2">
                                <div className="mb-5">
                                    <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Hoe lang moet de link werken?</Label>
                                    <div className="grid grid-cols-3 gap-2" data-testid="preview-share-expiry-options">
                                        {EXPIRY_OPTIONS.map((opt) => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => setSelectedDays(opt.value)}
                                                className={`h-10 text-xs rounded-sm border transition active:scale-95 ${selectedDays === opt.value ? "border-[#E5A93D] bg-[#E5A93D]/15 text-[#E5A93D] font-semibold" : "border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}
                                                data-testid={`preview-share-expiry-${opt.value}`}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="text-center">
                                    <Button
                                        onClick={generate}
                                        disabled={generating}
                                        className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm h-10 px-6"
                                        data-testid="preview-share-generate-btn"
                                    >
                                        {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Deellink aanmaken"}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Publieke link</Label>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            readOnly
                                            value={publicUrl}
                                            onClick={(e) => e.target.select()}
                                            className="h-10 bg-[#F4F4F5] border-zinc-200 rounded-sm text-xs font-mono text-[#E5A93D] focus-visible:border-[#E5A93D]"
                                            data-testid="preview-share-link-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={copy}
                                            className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs px-3 h-10 border border-[#E5A93D]/40 text-[#E5A93D] hover:bg-[#E5A93D]/10 rounded-sm transition active:scale-95"
                                            data-testid="preview-share-copy-btn"
                                        >
                                            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                                            {copied ? "Gekopieerd" : "Kopieer"}
                                        </button>
                                        <a
                                            href={publicUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs px-3 h-10 border border-zinc-200 hover:bg-zinc-100 text-zinc-600 rounded-sm transition"
                                        >
                                            <LinkIcon className="h-3.5 w-3.5" /> Open
                                        </a>
                                    </div>
                                </div>

                                <div data-testid="preview-share-stats" className={`text-xs px-3 py-2.5 rounded-sm border ${viewCount > 0 ? "bg-emerald-500/5 border-emerald-500/20 text-emerald-700" : "bg-[#F4F4F5] border-zinc-200 text-zinc-500"}`}>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Eye className="h-3.5 w-3.5 flex-shrink-0" />
                                        {viewCount > 0 ? (
                                            <>
                                                <span className="font-semibold text-zinc-950">{viewCount}</span>
                                                <span>{viewCount === 1 ? "weergave" : "weergaven"}</span>
                                                {lastViewedAt && (
                                                    <span className="text-zinc-500">· laatst bekeken {fmtRelative(lastViewedAt)}</span>
                                                )}
                                            </>
                                        ) : (
                                            <span>Nog niet bekeken</span>
                                        )}
                                    </div>
                                </div>

                                <div data-testid="preview-share-expiry-block">
                                    <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-1.5">
                                        <Clock className="h-3 w-3" /> Geldigheid
                                    </Label>
                                    <div className={`text-xs px-3 py-2 rounded-sm mb-2 ${isExpired ? "bg-red-500/10 border border-red-500/30 text-red-700" : expiryLabel ? "bg-[#F4F4F5] border border-zinc-200 text-zinc-600" : "bg-emerald-500/5 border border-emerald-500/20 text-emerald-700"}`} data-testid="preview-share-expiry-status">
                                        {isExpired ? (
                                            <span className="inline-flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Verlopen op {expiryLabel} — kies hieronder een nieuwe periode om opnieuw te activeren.</span>
                                        ) : expiryLabel ? (
                                            <>Verloopt op <span className="text-zinc-950 font-medium">{expiryLabel}</span>{left !== null && left >= 0 ? <span className="text-zinc-500"> · nog {left} {left === 1 ? "dag" : "dagen"}</span> : null}</>
                                        ) : (
                                            <>Deze link blijft <span className="font-medium">onbeperkt</span> geldig.</>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        {EXPIRY_OPTIONS.map((opt) => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => updateExpiry(opt.value)}
                                                disabled={updatingExpiry}
                                                className="h-9 text-[11px] rounded-sm border border-zinc-200 text-zinc-600 hover:border-[#E5A93D]/40 hover:text-[#E5A93D] transition active:scale-95 disabled:opacity-50"
                                                data-testid={`preview-share-update-${opt.value}`}
                                            >
                                                {opt.value === 0 ? "Nooit" : `+${opt.label}`}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="text-xs text-zinc-500 leading-relaxed bg-[#F4F4F5] border border-zinc-200 rounded-sm p-3">
                                    {asset === "photos"
                                        ? "De ontvanger kan alle foto's bekijken en in één keer downloaden als ZIP-bestand. Geen login nodig."
                                        : asset === "final_video" || asset === "reel"
                                        ? "De ontvanger kan de afgewerkte video bekijken en downloaden via deze link. Geen login nodig."
                                        : "De ontvanger ziet de preview-video en kan opmerkingen plaatsen op een specifieke tijd. Elke opmerking verschijnt direct in dit project (zichtbaar voor jou als admin én voor de makelaar) en stuurt een notificatie."}
                                </div>
                                <div className="flex items-center justify-between pt-2">
                                    <button
                                        onClick={revoke}
                                        disabled={revoking}
                                        className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700"
                                        data-testid="preview-share-revoke-btn"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                        {revoking ? "Intrekken…" : "Deellink intrekken"}
                                    </button>
                                    <Button onClick={() => setOpen(false)} variant="ghost" className="text-zinc-600 hover:text-zinc-950">
                                        Klaar
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
